import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, errorText } from "@/lib/api";
import { imgSrc } from "@/lib/media";

const TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export function ImageUpload({ value, onChange, t, testid, aspect = "4/3", fit = "cover" }) {
  const input = useRef(null);
  const [progress, setProgress] = useState(null);
  const pick = () => input.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!TYPES.includes(f.type)) return toast.error(t("up.type"));
    if (f.size > 8 * 1024 * 1024) return toast.error(t("up.size"));
    const fd = new FormData();
    fd.append("file", f);
    setProgress(0);
    try {
      const r = await api.post("/admin/upload", fd, { onUploadProgress: (p) => setProgress(Math.round((100 * p.loaded) / (p.total || 1))) });
      onChange(r.data.url);
      toast.success(t("up.done"));
    } catch (err) {
      toast.error(errorText(err, t("up.failed")));
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="relative overflow-hidden rounded-xl border border-dashed bg-secondary/40" style={{ aspectRatio: aspect }}>
        {value ? (
          <img src={imgSrc(value, "md")} alt="" data-testid={`${testid}-preview`} className={`absolute inset-0 h-full w-full ${fit === "contain" ? "object-contain p-3" : "object-cover"}`} />
        ) : (
          <button type="button" onClick={pick} className="absolute inset-0 grid place-items-center text-muted-foreground hover:text-foreground" data-testid={`${testid}-empty`}>
            <span className="flex flex-col items-center gap-2 text-xs"><ImagePlus className="h-6 w-6" />{t("up.choose")}</span>
          </button>
        )}
        {progress !== null && (
          <div className="absolute inset-x-0 bottom-0 space-y-1 bg-black/75 p-2" data-testid={`${testid}-progress`}>
            <Progress value={progress} className="h-1.5" />
            <div className="flex items-center justify-center gap-1 text-[11px]"><Loader2 className="h-3 w-3 animate-spin" />{progress}%</div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={pick} disabled={progress !== null} data-testid={`${testid}-pick-btn`}>{value ? t("up.replace") : t("up.upload")}</Button>
        {value && <Button type="button" size="sm" variant="ghost" onClick={() => onChange(null)} data-testid={`${testid}-remove-btn`}>{t("up.remove")}</Button>}
      </div>
      <input ref={input} type="file" accept={TYPES.join(",")} className="hidden" onChange={onFile} data-testid={`${testid}-input`} />
    </div>
  );
}
